import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { FormErrors } from '../types';
import { toQrDataUrl } from '../../../lib/qr';

interface TwoFactorSetupFormProps {
  issuer: string;
  manualKey: string;
  otpauthUri: string;
  onSubmit: (otp: string) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

const TwoFactorSetupForm: React.FC<TwoFactorSetupFormProps> = ({
  issuer,
  manualKey,
  otpauthUri,
  onSubmit,
  onBack,
  isLoading,
}) => {
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    setQrUrl(null);
    const uri = String(otpauthUri || '').trim();
    if (!uri) return;
    (async () => {
      try {
        const url = await toQrDataUrl(uri, 240);
        if (!canceled) setQrUrl(url);
      } catch {
        if (!canceled) setQrUrl(null);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [otpauthUri]);

  const displayKey = useMemo(() => {
    const k = (manualKey || '').replace(/\s/g, '').toUpperCase();
    return k.replace(/(.{4})/g, '$1 ').trim();
  }, [manualKey]);

  const validate = (): boolean => {
    const code = (otp || '').replace(/\s/g, '');
    const newErrors: FormErrors = {};
    if (!code) newErrors.otp = 'Codul este obligatoriu';
    else if (!/^\d{6}$/.test(code)) newErrors.otp = 'Introdu codul de 6 cifre';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit((otp || '').replace(/\s/g, ''));
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">Configurare 2FA (prima autentificare)</div>
        <div className="text-xs text-muted-foreground">
          Deschide Google Authenticator / Microsoft Authenticator și adaugă un cont nou.
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background p-4 space-y-2">
        {qrUrl ? (
          <div className="flex flex-col items-center gap-2">
            <div className="text-xs text-muted-foreground">Scanează codul QR în Authenticator</div>
            <img
              src={qrUrl}
              alt="Cod QR 2FA"
              className="w-[240px] h-[240px] rounded-lg border border-border bg-white p-2"
            />
          </div>
        ) : null}

        <div className="text-xs text-muted-foreground">Issuer</div>
        <div className="text-sm font-semibold text-foreground">{issuer}</div>
        <div className="mt-3 text-xs text-muted-foreground">Cheie (manual)</div>
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-sm text-foreground break-all">{displayKey}</div>
          <Button type="button" variant="outline" size="sm" onClick={() => copy((manualKey || '').trim())}>
            Copiază
          </Button>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">Link (otpauth://)</div>
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono text-[11px] text-muted-foreground break-all">{otpauthUri}</div>
          <Button type="button" variant="outline" size="sm" onClick={() => copy((otpauthUri || '').trim())}>
            Copiază
          </Button>
        </div>
      </div>

      <Input
        type="text"
        label="Cod OTP"
        placeholder="ex: 123456"
        inputMode="numeric"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        error={errors.otp}
        required
        disabled={isLoading}
        className="w-full"
      />

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack} disabled={isLoading} fullWidth>
          Înapoi
        </Button>
        <Button type="submit" variant="default" loading={isLoading} fullWidth>
          Activează 2FA
        </Button>
      </div>
    </form>
  );
};

export default TwoFactorSetupForm;
